import React, { useEffect, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { RadioButton } from "primereact/radiobutton";
import { invoiceService } from "../../Services";

const GenerateInvoiceModal = ({ visible, onHide, onSubmit, onSubmitBulk }) => {
  const [mode, setMode] = useState("individual");
  const [customers, setCustomers] = useState([]);
  const [fuelCompanies, setFuelCompanies] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [fuelCompany, setFuelCompany] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [dateDue, setDateDue] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [currency, setCurrency] = useState("CAD");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currencyOptions = [
    { label: "CAD", value: "CAD" },
    { label: "USD", value: "USD" },
  ];

  useEffect(() => {
    if (!visible) return;
    invoiceService.fetchCompanyCustomers().then((data) => {
      const opts = (data?.customers || data || []).map((c) => ({
        label: c.customer_name || c.name,
        value: c.customer_id || c.id,
      }));
      setCustomers(opts);
    });
    invoiceService.fetchFuelCompanies().then((data) => {
      const seen = new Set();
      const opts = (Array.isArray(data) ? data : (data?.fuel_company || []))
        .filter((c) => c.fuel_company_provider)
        .reduce((acc, c) => {
          const val = c.fuel_company_provider;
          if (!seen.has(val)) {
            seen.add(val);
            acc.push({ label: val, value: val });
          }
          return acc;
        }, []);
      setFuelCompanies(opts);
    });
  }, [visible]);

  const resetState = () => {
    setMode("individual");
    setCustomerId(null);
    setFuelCompany(null);
    setDateFrom(null);
    setDateTo(null);
    setDateDue(null);
    setNotifyEmail("");
    setCurrency("CAD");
  };

  const handleHide = () => {
    resetState();
    onHide();
  };

  // Send local date as midnight string — avoids UTC offset shifting the date
  const toMidnight = (d) => {
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T00:00:00`;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (mode === "individual") {
      await onSubmit({
        customer_id: customerId,
        fuel_source_company_param: fuelCompany,
        input_currency: currency,
        start_date: toMidnight(dateFrom),
        end_date: toMidnight(dateTo),
        due_date: dateDue ? toMidnight(dateDue) : null,
      });
    } else {
      await onSubmitBulk({
        fuel_source_company_param: fuelCompany,
        input_currency: currency,
        start_date: toMidnight(dateFrom),
        end_date: toMidnight(dateTo),
        due_date: dateDue ? toMidnight(dateDue) : null,
        notify_email: notifyEmail || null,
      });
    }
    setIsSubmitting(false);
    handleHide();
  };

  const isValid = mode === "individual"
    ? (customerId && fuelCompany && dateFrom && dateTo && dateDue)
    : (fuelCompany && dateFrom && dateTo && dateDue);

  return (
    <Sidebar
      header="Generate Invoices"
      visible={visible}
      position="right"
      style={{ width: "32vw" }}
      onHide={handleHide}
    >
      <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div style={{ display: "flex", gap: "1.5rem", alignItems: "center" }}>
          <div className="p-field-radiobutton" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <RadioButton inputId="mode_individual" value="individual" onChange={(e) => setMode(e.value)} checked={mode === "individual"} />
            <label htmlFor="mode_individual">Individual Customer</label>
          </div>
          <div className="p-field-radiobutton" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <RadioButton inputId="mode_all" value="all" onChange={(e) => setMode(e.value)} checked={mode === "all"} />
            <label htmlFor="mode_all">All Customers</label>
          </div>
        </div>
        {mode === "individual" && (
          <div>
            <label>Customer</label>
            <Dropdown
              value={customerId}
              options={customers}
              onChange={(e) => setCustomerId(e.value)}
              placeholder="Select customer"
              filter
            />
          </div>
        )}
        <div>
          <label>Fuel Company</label>
          <Dropdown
            value={fuelCompany}
            options={fuelCompanies}
            onChange={(e) => setFuelCompany(e.value)}
            placeholder="Select fuel company"
          />
        </div>
        <div>
          <label>Currency</label>
          <Dropdown
            value={currency}
            options={currencyOptions}
            onChange={(e) => setCurrency(e.value)}
          />
        </div>
        <div>
          <label>Date From</label>
          <Calendar
            value={dateFrom}
            onChange={(e) => setDateFrom(e.value)}
            placeholder="Select start date"
            showIcon
          />
        </div>
        <div>
          <label>Date To</label>
          <Calendar
            value={dateTo}
            onChange={(e) => setDateTo(e.value)}
            placeholder="Select end date"
            showIcon
          />
        </div>
        <div>
          <label>Due Date</label>
          <Calendar
            value={dateDue}
            onChange={(e) => setDateDue(e.value)}
            placeholder="Select due date"
            showIcon
          />
        </div>
        {mode === "all" && (
          <div>
            <label>Notify Email (optional)</label>
            <input
              type="email"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="email@example.com"
              className="p-inputtext p-component"
              style={{ width: "100%" }}
            />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
          <Button label="Cancel" className="p-button-text" onClick={handleHide} />
          <Button
            label={isSubmitting ? "Generating..." : "Generate"}
            icon="pi pi-file"
            className="p-button-success"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          />
        </div>
      </div>
    </Sidebar>
  );
};

export default GenerateInvoiceModal;
